import React from 'react';
import classnames from 'classnames';
import { utils } from '@ohif/core';
import { Separator } from '@ohif/ui-next';
import { useImportedStudyStore } from '../../stores/useImportedStudyStore';

const { formatDate } = utils;

type ImportedStudyPanelProps = {
  onSelectStudy: (studyInstanceUID: string) => void;
};

function truncateStudyInstanceUID(studyInstanceUID: string): string {
  if (studyInstanceUID.length <= 24) {
    return studyInstanceUID;
  }

  return `${studyInstanceUID.slice(0, 10)}…${studyInstanceUID.slice(-8)}`;
}

function ImportedStudyPanel({ onSelectStudy }: ImportedStudyPanelProps) {
  const studies = useImportedStudyStore(state => state.studies);
  const activeStudyInstanceUID = useImportedStudyStore(state => state.activeStudyInstanceUID);

  if (!studies.length) {
    return null;
  }

  return (
    <div
      className="bg-background border-viewer-border flex flex-col gap-2 border-b px-3 py-3"
      data-cy="imported-study-panel"
    >
      <div className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
        Imported Studies ({studies.length})
      </div>

      <div className="flex flex-col gap-1.5">
        {studies.map(study => {
          const isActive = study.studyInstanceUID === activeStudyInstanceUID;
          const formattedDate = formatDate(study.studyDate) || study.studyDate || 'Unknown date';

          return (
            <button
              key={study.studyInstanceUID}
              type="button"
              data-cy={`imported-study-item-${study.studyInstanceUID}`}
              className={classnames(
                'hover:bg-accent w-full rounded-md border px-3 py-2 text-left transition-colors',
                isActive
                  ? 'border-primary bg-primary/5 ring-primary/30 ring-1'
                  : 'border-border bg-popover'
              )}
              onClick={() => onSelectStudy(study.studyInstanceUID)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-foreground truncate text-[13px] font-semibold">
                    {formattedDate}
                  </div>
                  <div className="text-muted-foreground truncate text-[12px]">
                    {study.studyDescription || 'Imported Study'}
                  </div>
                </div>
                <div className="text-muted-foreground shrink-0 text-right text-[11px]">
                  <div className="max-w-[72px] truncate">{study.modalities || '—'}</div>
                  <div className="tabular-nums">{study.numInstances}</div>
                </div>
              </div>
              <div
                className="text-muted-foreground mt-1 truncate font-mono text-[10px]"
                title={study.studyInstanceUID}
              >
                {truncateStudyInstanceUID(study.studyInstanceUID)}
              </div>
            </button>
          );
        })}
      </div>

      <Separator
        orientation="horizontal"
        className="bg-viewer-border"
        thickness="1px"
      />
    </div>
  );
}

export default ImportedStudyPanel;
